import * as acorn from 'acorn';

/**
 * Trouve la ligne d'erreur dans le code
 * @param {string} code - Le code à analyser
 * @returns {number|null} - Le numéro de ligne de l'erreur ou null
 */
export const findErrorLine = (code) => {
  try {
    // Essayer de parser le code avec Acorn
    acorn.parse(code, {
      ecmaVersion: 2020,
      locations: true
    });
  } catch (e) {
    if (e.loc) {
      // Pour les erreurs de parenthèses, accolades ou crochets non fermés,
      // vérifier la ligne précédente
      if (e.message.includes('Unexpected token') || 
          e.message.includes('Unexpected end of input')) {
        
        // Obtenir la ligne mentionnée et la ligne précédente
        const lines = code.split('\n');
        const errorLine = e.loc.line;
        
        if (errorLine > 1) {
          const currentLine = lines[errorLine - 1] || '';
          const prevLine = lines[errorLine - 2] || '';
          
          // Vérifier si la ligne précédente contient des parenthèses, crochets ou accolades non fermés
          const openParens = (prevLine.match(/\(/g) || []).length;
          const closeParens = (prevLine.match(/\)/g) || []).length;
          const openBrackets = (prevLine.match(/\[/g) || []).length;
          const closeBrackets = (prevLine.match(/\]/g) || []).length;
          const openBraces = (prevLine.match(/{/g) || []).length;
          const closeBraces = (prevLine.match(/}/g) || []).length;
          
          // Si la ligne précédente a des délimiteurs non fermés et la ligne courante ne les ferme pas
          if ((openParens > closeParens) || 
              (openBrackets > closeBrackets) || 
              (openBraces > closeBraces)) {
            return errorLine - 1; // Retourner la ligne précédente
          }
          
          // Détecter les parenthèses ou virgules manquantes dans les appels de fonction
          if (prevLine.includes('(') && !prevLine.includes(')') && !prevLine.endsWith(';')) {
            return errorLine - 1;
          }
        }
      }
      return e.loc.line; // Retourne le numéro de ligne si disponible
    }
  }
  return null;
};

/**
 * Configure Monaco Editor avec les typages p5.js
 * @param {Object} editor - L'instance de l'éditeur
 * @param {Object} monaco - L'instance de Monaco
 */
export const setupMonacoWithP5Types = (editor, monaco) => {
  // URLs des fichiers de typage (utilisez vos fichiers locaux ou des URLs externes)
  const p5TypingsUrls = ['/types/global.d.ts', '/types/index.d.ts'];
  
  // Configurer les options du compilateur TypeScript
  monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
    allowJs: true,
    checkJs: false, // Désactiver la vérification TypeScript pour les fichiers JS
    noLib: true, // ⛔️ enlève les types natifs JS (ex: console, Math, etc.)
    target: monaco.languages.typescript.ScriptTarget.ES2020,
    allowNonTsExtensions: true,
    moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
    module: monaco.languages.typescript.ModuleKind.CommonJS,
    noEmit: true,
    typeRoots: ["node_modules/@types"],
    // Désactiver les warnings spécifiques pour les débutants
    noImplicitAny: false, // Pas de warning pour les paramètres implicites 'any'
    strict: false, // Désactiver le mode strict
    noImplicitReturns: false,
    noImplicitThis: false,
    noUnusedLocals: false,
    noUnusedParameters: false
  });

  // Désactiver les diagnostics TypeScript pour les erreurs de type "any"
  monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: false,
    noSyntaxValidation: false,
    // Désactiver des codes d'erreur spécifiques
    diagnosticCodesToIgnore: [
      7044, // Parameter implicitly has an 'any' type
      7006, // Parameter implicitly has an 'any' type  
      7031, // Binding element implicitly has an 'any' type
      2304, // Cannot find name (pour éviter les erreurs sur les variables non déclarées)
      2322, // Type assignment errors
      2339, // Property does not exist on type
      2345, // Argument of type assignment errors
    ]
  });
  
  // Charger les typages et les ajouter à Monaco
  Promise.all(p5TypingsUrls.map(url => fetch(url).then(res => res.text())))
    .then(([globalTypes, indexTypes]) => {
      // Ajouter les librairies de typage
      monaco.languages.typescript.javascriptDefaults.addExtraLib(
        globalTypes,
        'global.d.ts'
      );
      monaco.languages.typescript.javascriptDefaults.addExtraLib(
        indexTypes,
        'index.d.ts'
      );
      
      //console.log('p5.js typings loaded successfully');
    })
    .catch(error => {
      console.error('Failed to load p5.js typings:', error);
    });
};

/**
 * Crée un objet console personnalisé pour capturer les logs
 * @param {Function} logCallback - Fonction pour traiter les logs
 * @returns {Object} - Objet console personnalisé
 */
export const createCustomConsole = (logCallback) => {
  return {
    log: (...args) => {
      // Log original dans la console du navigateur
      console.log(...args);
      
      // Ajouter le log aux logs affichés
      const formattedLog = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ');
      
      logCallback(formattedLog);
    },
    error: (...args) => {
      // Log original dans la console du navigateur
      console.error(...args);
      
      // Ajouter le log d'erreur avec un préfixe
      const formattedLog = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ');
      
      logCallback(`Erreur: ${formattedLog}`);
    },
    warn: (...args) => {
      // Log original dans la console du navigateur
      console.warn(...args);
      
      // Ajouter le log d'avertissement avec un préfixe
      const formattedLog = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ');
      
      logCallback(`Avertissement: ${formattedLog}`);
    }
  };
};


/**
 * Configure p5.js pour rediriger les appels loadImage et loadSound vers le dossier d'assets du projet
 * @param {Object} p - L'instance p5
 * @param {string} userId - ID de l'utilisateur
 * @param {string} projectId - ID du projet
 * @param {Function} errorCallback - Fonction à appeler en cas d'erreur
 */
export const setupAssetPathRedirection = (p, workdir, errorCallback) => {
  // Créer le préfixe de chemin pour les assets
  let assetBasePath = "";
  if (workdir) {
    assetBasePath = `/assets/${workdir}/`;
  } else {
    assetBasePath = `/assets/shared/`;
  }
  
  // Fonction utilitaire pour rediriger les chemins et gérer les erreurs
  const redirectAssetPath = (originalFn, path, successCallback, failureCallback, assetType) => {
    path = assetBasePath + path;    
    
    // Créer un callback personnalisé pour capturer les erreurs
    const customFailureCallback = function(event) {
      console.error(`Chargement du ${assetType}: ${path} introuvable`, event);
      
      // Appeler le callback d'erreur pour afficher dans la console utilisateur
      const errorMessage = `Chargement ${assetType}: "${path.split('/').pop()}" introuvable`;
      errorCallback(null, errorMessage, null);
      
      // Appeler aussi le callback d'échec original s'il existe
      if (typeof failureCallback === 'function') {
        failureCallback(event);
      }
    };
    
    // Appeler la fonction originale avec le chemin modifié
    return originalFn.call(p, path, successCallback, customFailureCallback);
  };
  
  // Définir les méthodes de chargement et les types d'assets correspondants
  const loadingMethods = [
    { method: 'loadImage', assetType: "de l'image" },
    { method: 'loadSound', assetType: "du son" },
    { method: 'loadFont', assetType: "de la police" },
    { method: 'loadJSON', assetType: "du fichier JSON" },
    { method: 'loadStrings', assetType: "du fichier texte" },
    { method: 'loadTable', assetType: "du tableau" },
    { method: 'loadXML', assetType: "du fichier XML" },
    { method: 'loadBytes', assetType: "des données binaires" },
    { method: 'loadModel', assetType: "du modèle 3D" },
    { method: 'loadShader', assetType: "du shader" }
  ];
  
  // Remplacer chaque méthode disponible
  loadingMethods.forEach(({ method, assetType }) => {
    // Vérifier si la méthode existe dans l'instance p5
    if (typeof p[method] === 'function') {
      // Sauvegarder la fonction originale
      const originalFn = p[method];
      
      // Remplacer par notre fonction avec redirection
      p[method] = function(path, successCallback, failureCallback) {
        return redirectAssetPath(originalFn, path, successCallback, failureCallback, assetType);
      };
    }
  });
  p.assetBasePath = assetBasePath;
  
  //console.log(`Assets path configured to: ${assetBasePath}`);
};

/**
 * Crée un wrapper pour gérer les erreurs dans les fonctions
 * @param {Function} fn - La fonction à wrapper
 * @param {string} functionName - Nom de la fonction pour le message d'erreur
 * @param {Function} errorCallback - Fonction à appeler en cas d'erreur
 * @param {boolean} stopLoopOnError - Arrêter la boucle en cas d'erreur
 * @param {Object} options - Options supplémentaires (userId, projectId)
 * @returns {Function} - Fonction wrapper
 */
export const createErrorHandlingWrapper = (fn, functionName, errorCallback, stopLoopOnError = false, options = {}) => {
  return function() {
    try {
      // Configurer la capture d'erreurs d'assets si c'est la fonction setup      
      // Exécuter la fonction
      const result = fn.apply(this, arguments);
      return result;
    } catch (error) {
      // Extraire la ligne de l'erreur à partir de la stack si disponible
      let errorLine = null;
      if (error.stack) {
        const match = error.stack.match(/userCode\.js:(\d+):(\d+)/);
        if (match) {
          errorLine = parseInt(match[1]) - 6; // index 0-based
        }
      }
      
      // Arrêter la boucle si c'est une fonction draw et stopLoopOnError est true
      if (stopLoopOnError && this && typeof this.noLoop === 'function') {
        try {
          this.noLoop();
        } catch (e) {
          console.error("Erreur lors de l'arrêt de la boucle:", e);
        }
      }
      
      // Appeler le callback d'erreur
      errorCallback(errorLine, error.message, error.stack);
      
      // Empêcher la propagation de l'erreur
      return false;
    }
  };
}; 